"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

export type TourPhase =
  | "idle"
  | "home"
  | "home-search-open"
  | "home-search-typing"
  | "home-search-pick"
  | "product"
  | "shop"
  | "complete";

export const TOTAL_TOUR_STEPS = 9;

type TutorialContextValue = {
  enabled: boolean;
  showBanner: boolean;
  tourActive: boolean;
  currentPhase: TourPhase;
  currentStep: number;
  isTransitioning: boolean;
  showWelcome: boolean;
  showExitConfirm: boolean;
  showCompletion: boolean;
  startTour: () => void;
  beginTourAfterWelcome: () => void;
  dismissBanner: () => void;
  setPhase: (phase: TourPhase) => void;
  setStep: (step: number) => void;
  setTransitioning: (v: boolean) => void;
  setShowExitConfirm: (v: boolean) => void;
  setShowCompletion: (v: boolean) => void;
  completeTour: () => void;
  /** User-initiated exit (no celebration). Resets all tour state cleanly. */
  exitTour: () => void;
  restartTour: () => void;
  openSearch: (() => void) | null;
  registerOpenSearch: (fn: () => void) => void;
};

const TutorialContext = createContext<TutorialContextValue | null>(null);

const LS_KEY = "bp-tutorial-seen";
const SS_PHASE_KEY = "bp-tutorial-phase";

function isFeatureEnabled() {
  return process.env.NEXT_PUBLIC_SHOW_TUTORIAL === "true";
}

export function TutorialProvider({ children }: { children: ReactNode }) {
  const enabled = isFeatureEnabled();

  const [showBanner, setShowBanner] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<TourPhase>("idle");
  const [currentStep, setCurrentStepState] = useState(1);
  const [isTransitioning, setTransitioning] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [openSearchFn, setOpenSearchFn] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const seen = localStorage.getItem(LS_KEY);
    if (seen === "true") return;

    const savedPhase = sessionStorage.getItem(SS_PHASE_KEY) as TourPhase | null;
    if (savedPhase && savedPhase !== "idle" && savedPhase !== "complete") {
      setCurrentPhase(savedPhase);
      setTourActive(true);
      return;
    }

    const timer = setTimeout(() => setShowBanner(true), 3000);
    return () => clearTimeout(timer);
  }, [enabled]);

  const startTour = useCallback(() => {
    setShowBanner(false);
    setShowWelcome(true);
  }, []);

  const beginTourAfterWelcome = useCallback(() => {
    setShowWelcome(false);
    setTourActive(true);
    setCurrentPhase("home");
    setCurrentStepState(1);
    sessionStorage.setItem(SS_PHASE_KEY, "home");
  }, []);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setShowWelcome(false);
    localStorage.setItem(LS_KEY, "true");
  }, []);

  const setPhase = useCallback((phase: TourPhase) => {
    setCurrentPhase(phase);
    sessionStorage.setItem(SS_PHASE_KEY, phase);
    if (phase === "complete") {
      setTourActive(false);
      localStorage.setItem(LS_KEY, "true");
      sessionStorage.removeItem(SS_PHASE_KEY);
    }
  }, []);

  const setStep = useCallback((step: number) => {
    setCurrentStepState(step);
  }, []);

  const completeTour = useCallback(() => {
    setTourActive(false);
    setCurrentPhase("complete");
    setShowExitConfirm(false);
    localStorage.setItem(LS_KEY, "true");
    sessionStorage.removeItem(SS_PHASE_KEY);
    setShowCompletion(true);
  }, []);

  /**
   * User abandoned the tour. Reset everything to idle without firing the
   * celebration modal — the user already made the choice; respect it.
   */
  const exitTour = useCallback(() => {
    setTourActive(false);
    setCurrentPhase("idle");
    setCurrentStepState(1);
    setShowExitConfirm(false);
    setShowCompletion(false);
    setShowWelcome(false);
    setShowBanner(false);
    setTransitioning(false);
    localStorage.setItem(LS_KEY, "true");
    sessionStorage.removeItem(SS_PHASE_KEY);
  }, []);

  const restartTour = useCallback(() => {
    localStorage.removeItem(LS_KEY);
    sessionStorage.removeItem(SS_PHASE_KEY);
    setShowBanner(false);
    setShowCompletion(false);
    setShowExitConfirm(false);
    setTransitioning(false);
    setShowWelcome(true);
    setCurrentStepState(1);
    setTourActive(false);
    setCurrentPhase("idle");
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }
  }, []);

  const registerOpenSearch = useCallback((fn: () => void) => {
    setOpenSearchFn(() => fn);
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        enabled,
        showBanner,
        tourActive,
        currentPhase,
        currentStep,
        isTransitioning,
        showWelcome,
        showExitConfirm,
        showCompletion,
        startTour,
        beginTourAfterWelcome,
        dismissBanner,
        setPhase,
        setStep,
        setTransitioning,
        setShowExitConfirm,
        setShowCompletion,
        completeTour,
        exitTour,
        restartTour,
        openSearch: openSearchFn,
        registerOpenSearch,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error("useTutorial must be used within TutorialProvider");
  return ctx;
}
